import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const NETWORK_TOPOLOGIES = Object.freeze(['chain', 'parallel', 'pairs', 'stack'] as const);
export type NetworkTopology = (typeof NETWORK_TOPOLOGIES)[number];
export interface OperatorSettings { readonly ratio?: number; readonly level?: number; readonly index?: number; }
export interface FmPmNetworkOptions { readonly sampleRate: number; readonly frequency?: number; readonly topology?: NetworkTopology; readonly feedback?: number; readonly outputGain?: number; }
interface OperatorState { ratio: number; level: number; index: number; phase: number; previous: number; }
const TAU = Math.PI * 2;
const bounded = (name: string, value: number, min: number, max: number): void => { if (!Number.isFinite(value) || value < min || value > max) throw new RangeError(`${name} must be in [${min}, ${max}]`); };

/** Eight-operator sine PM network with four fixed acyclic algorithms and bounded local feedback. */
export class FmPmNetwork {
  readonly sampleRate: number;
  private frequency: number;
  private topology: NetworkTopology;
  private feedback: number;
  private outputGain: number;
  private readonly operators: OperatorState[];

  constructor(options: FmPmNetworkOptions) {
    bounded('sampleRate', options.sampleRate, 1, 768_000); this.sampleRate = options.sampleRate;
    this.frequency = options.frequency ?? 220; this.topology = options.topology ?? 'chain';
    this.feedback = options.feedback ?? 0; this.outputGain = options.outputGain ?? 0.8;
    this.operators = Array.from({ length: 8 }, (_, i) => ({ ratio: i === 0 ? 1 : i + 1, level: i === 0 ? 1 : 0, index: 0, phase: 0, previous: 0 }));
    this.validate();
  }
  reset(): void { for (const op of this.operators) { op.phase = 0; op.previous = 0; } }
  setOperator(index: number, settings: OperatorSettings): void {
    if (!Number.isInteger(index) || index < 0 || index >= 8) throw new RangeError('operator index must be in [0, 7]');
    if (settings.ratio !== undefined) bounded('ratio', settings.ratio, 0.03125, 32);
    if (settings.level !== undefined) bounded('level', settings.level, 0, 1);
    if (settings.index !== undefined) bounded('index', settings.index, 0, 16);

    const op = this.operators[index]!;
    if (settings.ratio !== undefined) op.ratio = settings.ratio;
    if (settings.level !== undefined) op.level = settings.level;
    if (settings.index !== undefined) op.index = settings.index;
  }
  setParameter(id: string, value: number): void {
    if (id === 'frequency') { bounded(id, value, 0.1, 20_000); this.frequency = value; return; }
    if (id === 'topology') { if (!Number.isInteger(value) || !NETWORK_TOPOLOGIES[value]) throw new RangeError('topology must be in [0, 3]'); this.topology = NETWORK_TOPOLOGIES[value]!; return; }
    if (id === 'feedback') { bounded(id, value, 0, 1); this.feedback = value; return; }
    if (id === 'output-gain') { bounded(id, value, 0, 0.95); this.outputGain = value; return; }
    const match = /^op([1-8])-(ratio|level|index)$/.exec(id);
    if (match) { this.setOperator(Number(match[1]) - 1, { [match[2]!]: value }); return; }
    throw new RangeError(`unknown parameter: ${id}`);
  }
  processInto(output: Float32Array): void {
    for (let frame = 0; frame < output.length; frame++) {
      const raw = this.topology === 'parallel' ? this.parallel() : this.topology === 'pairs' ? this.pairs() : this.topology === 'stack' ? this.stack() : this.chain();
      output[frame] = Math.max(-0.95, Math.min(0.95, raw * this.outputGain));
    }
  }
  private tick(index: number, modulation = 0): number {
    const op = this.operators[index]!; const phaseMod = modulation * op.index + (index === 0 ? op.previous * this.feedback : 0);
    const value = Math.sin(op.phase + phaseMod); op.phase = (op.phase + TAU * this.frequency * op.ratio / this.sampleRate) % TAU; op.previous = value; return value * op.level;
  }
  private chain(): number { let value = this.tick(7); for (let i = 6; i >= 0; i--) value = this.tick(i, value); return value; }
  private parallel(): number { let sum = 0; for (let i = 0; i < 8; i++) sum += this.tick(i); return sum / Math.sqrt(8); }
  private pairs(): number { let sum = 0; for (let i = 0; i < 8; i += 2) sum += this.tick(i, this.tick(i + 1)); return sum * 0.5; }
  private stack(): number { const a = this.tick(0, this.tick(1, this.tick(2, this.tick(3)))); const b = this.tick(4, this.tick(5, this.tick(6, this.tick(7)))); return (a + b) / Math.SQRT2; }
  private validate(): void { bounded('frequency', this.frequency, 0.1, 20_000); if (!NETWORK_TOPOLOGIES.includes(this.topology)) throw new RangeError('invalid topology'); bounded('feedback', this.feedback, 0, 1); bounded('output-gain', this.outputGain, 0, 0.95); }
}

class Instance implements DspModuleInstance {
  private readonly synth: FmPmNetwork;
  constructor(sampleRate: number, private readonly maxBlockSize: number) { this.synth = new FmPmNetwork({ sampleRate }); }
  reset(): void { this.synth.reset(); }
  setParameter(id: string, value: number): void { this.synth.setParameter(id, value); }
  process(block: DspProcessBlock): void { if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) throw new RangeError('frames exceeds maximum block size'); const out = block.outputs.out; if (!out || out.length < block.frames) throw new RangeError('out buffer missing or too short'); this.synth.processInto(out.subarray(0, block.frames)); }
}
const parameters = [
  { id: 'frequency', defaultValue: 220, minValue: 0.1, maxValue: 20_000, unit: 'Hz' },
  { id: 'topology', defaultValue: 0, minValue: 0, maxValue: 3, unit: 'enum' },
  { id: 'feedback', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'ratio' },
  { id: 'output-gain', defaultValue: 0.8, minValue: 0, maxValue: 0.95, unit: 'ratio' },
  ...Array.from({ length: 8 }, (_, i) => [
    { id: `op${i + 1}-ratio`, defaultValue: i === 0 ? 1 : i + 1, minValue: 0.03125, maxValue: 32, unit: 'ratio' },
    { id: `op${i + 1}-level`, defaultValue: i === 0 ? 1 : 0, minValue: 0, maxValue: 1, unit: 'ratio' },
    { id: `op${i + 1}-index`, defaultValue: 0, minValue: 0, maxValue: 16, unit: 'rad' },
  ]).flat(),
];
export const mod003 = defineDspModule({ descriptor: { id: 'mir.mod-003', name: 'FM/PM operator network', version: '0.1.0', inputs: [], outputs: [{ id: 'out', kind: 'audio', channels: 1 }], parameters }, createInstance(context) { if (!(context.sampleRate > 0) || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) throw new RangeError('invalid DSP context'); return new Instance(context.sampleRate, context.maxBlockSize); } });
